import { Component, Inject, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Module } from '../../../../models/entities/module';
import { JwtPermissionClaim } from '../../../../services/auth/jwt-permission-claim.model';
import { StringService } from '../../../../services/string.service';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UntypedFormBuilder } from '@angular/forms';
import { RequestFormService } from '../../../../services/forms/request-form.service';
import { GrowlerService } from '../../../../services/growler.service';
import { MembersService } from '../../../../services/members/members.service';
import { MemberPermissionPatchRequest, PermissionsService } from '../../../../services/permissions/permissions.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { Form } from '../../../../models/entities/forms/form';
import { MemberPermissionModuleItem } from '../member-permissions-dialog/member-permissions-dialog.component';
import { GenericDialogResult } from '../generic-mat-dialog/generic-dialog-result';
import { Membership } from '../../../../models/entities/membership';

export interface GroupPermissionDialogInput
{
    groupMembers: any[];
}

export interface GroupPermissionModuleItem
{
    module: Module;
    usersCanRead: boolean;
    usersCanWrite: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-group-permissions-dialog',
    templateUrl: './group-permissions-dialog.component.html',
    styleUrls: ['./group-permissions-dialog.component.scss'],
})
export class GroupPermissionsDialogComponent implements OnInit
{
    modules: Module[] = [];
    permissions: JwtPermissionClaim[] = [];
    stringService = StringService;
    moduleList: MemberPermissionModuleItem[] = [];

    groupMembers: Membership[] = [];
    orgForms: Form[] = [];

    adminForm = this._fb.group({
        isAdmin: [false],
    });

    isSaving = false;
    jwt: JwtLegFiClaims = null;

    constructor(
            private _dialogRef: MatDialogRef<GroupPermissionModuleItem>,
            private _fb: UntypedFormBuilder,
            private _formService: RequestFormService,
            private _growler: GrowlerService,
            private _membersService: MembersService,
            private _permissionsService: PermissionsService,
            @Inject(MAT_DIALOG_DATA) public data: GroupPermissionDialogInput,
    ) {
    }


    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();
        this.groupMembers = this.data.groupMembers || [];
        this.getOrgModules();
    }

    onSubmit() {
        this.isSaving = true;

        // save admin status either way
        this.saveAdminStatus();
    }

    /** Save admin status either way (some of them may have been admins, some maybe not) */
    saveAdminStatus() {
        this._permissionsService.setMultipleMemberAdminStatus(
                this.groupMembers[0].organizationId,
                this.adminForm.get('isAdmin').value,
                this.groupMembers.map(m => m.id))
                .subscribe(
                        {
                            next: () => {
                                // only save permissions if they are not admin
                                if (!this.adminForm.get('isAdmin').value) {
                                    this.savePermissions();
                                } else {
                                    this._growler.success('Success', 'Members saved as admin.');
                                    this._dialogRef.close(<GenericDialogResult>{success: true});
                                }
                            },
                            error: () => {
                                this._growler.error('Error', 'Could not save member as admin.');
                            },
                        },
                );
    }

    /** Save the permissions all at once. */
    savePermissions() {
        const request: MemberPermissionPatchRequest = {
            memberIds: this.groupMembers.map(m => m.id),
            permissions: this.moduleList.map(l => {
                return {
                    moduleId: l.module.id,
                    formId: l.module.formId || 0,
                    memberCanRead: l.memberCanRead,
                    memberCanWrite: l.memberCanWrite,
                };
            }),
        };

        this._permissionsService
                .patchMemberPermissions(this.groupMembers[0].organizationId, request)
                .subscribe(
                        {
                            next: () => {
                                this._growler.success('Success', 'Permissions saved.');
                                this._dialogRef.close(<GenericDialogResult>{success: true});
                            },
                            error: () => {
                                this._growler.error('Error', 'Could not save member permission.');
                            },
                        },
                );
    }

    getOrgModules(): void {
        this._permissionsService
                .getOrganizationModules(null)
                .pipe(untilDestroyed(this))
                .subscribe(
                        {
                            next: (modules: Module[]) => {
                                this.modules = modules.sort((a, b) => {
                                    return a.name.localeCompare(b.name);
                                });

                                this.getFormsWithCountsForOrg();
                            },
                            error: () => {
                                this._growler.error('Error', 'Unable to pull accessible module list.');
                            },
                        },
                );
    }


    /** Manipulate UI to toggle the corresponding 'write' checkbox related to this read checkbox */
    changeModuleReadPermission(item: MemberPermissionModuleItem, $event: MatCheckboxChange) {
        item.memberCanRead = $event.checked;

        // if checking to true, don't care what write does
        // if check to false, turn off write
        if (!$event.checked) {
            item.memberCanWrite = false;
        }

        // if toggling Custom Forms perms, adjust actual form perm's below that
        if (item.module.name === 'Custom Forms') {
            this.moduleList.forEach(formItem => {
                if (formItem.module.formId) {
                    formItem.memberCanRead = $event.checked;

                    if (!$event.checked) {
                        formItem.memberCanWrite = false;
                    }
                }
            });
        }

        // if toggling a specific form, adjust the Custom Forms module
        if (item.module.formId && !$event.checked) {
            this.moduleList.find(listItem => listItem.module.name === 'Custom Forms').memberCanRead = false;
            this.moduleList.find(listItem => listItem.module.name === 'Custom Forms').memberCanWrite = false;
        }
    }

    /** Manipulate UI to toggle the corresponding read checkbox related to this write checkbox */
    changeModuleWritePermission(item: MemberPermissionModuleItem, $event: MatCheckboxChange) {
        item.memberCanWrite = $event.checked;

        // if checking to true, write s/b true
        // if check to false, don't care what read does
        if ($event.checked) {
            item.memberCanRead = true;
        }

        // if read is disabled for the module, uncheck it if they uncheck write
        if (!$event.checked && item.module.disableRead) {
            item.memberCanRead = false;
        }

        // if toggling Custom Forms perms, adjust actual form perm's below that
        if (item.module.name === 'Custom Forms') {
            this.moduleList.forEach(formItem => {
                if (formItem.module.formId) {
                    formItem.memberCanWrite = $event.checked;

                    if ($event.checked) {
                        formItem.memberCanRead = true;
                    }
                }
            });
        }

        // if toggling a specific form, adjust the Custom Forms module
        if (item.module.formId && !$event.checked) {
            this.moduleList.find(listItem => listItem.module.name === 'Custom Forms').memberCanWrite = false;
        }
    }

    /** Update all the checkboxes based on if the person is toggling to admin or not */
    toggleAdminStatus(isChecked: boolean) {
        this.moduleList.forEach(l => l.memberCanRead = l.memberCanWrite = isChecked);
    }

    getFormsWithCountsForOrg() {
        this._formService.getFormsWithCountsForOrg().pipe(untilDestroyed(this))
                .subscribe((forms: Form[]) => {
                    this.orgForms = forms;
                    if (forms.length) {
                        let i = 4;
                        // forms come in with permissions as a related array
                        forms.forEach((f) => {
                            this.modules.splice(i, 0, {
                                id: -1,
                                siteId: 4,
                                name: 'Form: ' + f.name,
                                isEnabled: true,
                                formId: f.id,
                            });

                            i++;
                        });
                    }

                    this.moduleList = this.modules.map(module => {
                        return <MemberPermissionModuleItem>{
                            module: module,
                            memberCanRead: false,
                            memberCanWrite: false,
                        };
                    });
                });
    }

}
